import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { DateFormat, SemboDate } from '@libs/sembo-dates';
import {
  isFeatureToggleActive,
  CustomerOriginQueryParamValue,
} from '@libs/feature-toggle';

import { useCriteriaStore } from '../criteria/criteriaStore';
import { useItineraryStore } from '../itinerary/itineraryStore';
import { useMetaStore } from '../meta/metaStore';
import { usePremadeTripStore } from '../premadeTripStore';
import { useRecipeStore } from '../recipe/recipeStore';
import { useTripStore } from '../trip/tripStore';
import { useQueryParamsStore } from '../queryParamsStore';
import { PremadeTripType } from '../../models';

import { getRecipeForShareWidget } from './shareWidgetStoreLogic';

export const useShareWidgetStore = defineStore('ShareWidgetStore', () => {
  const recipeStore = useRecipeStore();
  const metaStore = useMetaStore();
  const itineraryStore = useItineraryStore();
  const tripStore = useTripStore();
  const criteriaStore = useCriteriaStore();
  const premadeTripStore = usePremadeTripStore();
  const queryParamsStore = useQueryParamsStore();

  // State
  const showModal = ref<boolean>(false);

  // Actions
  function openModal(): void {
    showModal.value = true;
  }

  function closeModal(): void {
    showModal.value = false;
  }

  // Getters
  const isRecipeValid = computed<boolean>(() => recipeStore.isRecipeCorrect);

  const recipe = computed<string>(() =>
    getRecipeForShareWidget(
      criteriaStore.itineraryCriteria,
      metaStore.metaAffiliateId,
      isFeatureToggleActive('shared-widget-clean-recipe')
    )
  );

  const price = computed<number | undefined>(
    () => tripStore.selectedItineraryWithStaticInfo?.totalPrice?.amount
  );

  const selectedDate = computed<string | null>(() =>
    itineraryStore.userSelectedItinerary?.date
      ? new SemboDate(itineraryStore.userSelectedItinerary?.date).format(
          DateFormat.DateReverse
        )
      : null
  );

  const publishedTripId = computed<string | undefined>(() => {
    if (
      premadeTripStore.premadeTrip?.type === PremadeTripType.PublishedInSanity
    ) {
      return queryParamsStore.queryParams?.publishedTripId;
    }

    return undefined;
  });

  const customerOrigin = computed<CustomerOriginQueryParamValue>(
    () => CustomerOriginQueryParamValue.CopiedShortenedUrl
  );

  return {
    // State
    showModal,

    // Actions
    openModal,
    closeModal,

    // Getters
    isRecipeValid,
    recipe,
    price,
    selectedDate,
    publishedTripId,
    customerOrigin,
  };
});
